/**
 * Las secciones de la landing V4 del Plano Google Maps, en el orden del diseño.
 *
 * Dos diseños en el lienzo y los dos aquí: el MÓVIL (`V4_Main`, columna de
 * 640 px) es la base, y desde `md` cada sección pasa al de ORDENADOR
 * (`V4_Desktop`), que no es el móvil estirado sino otra maquetación — cabecera
 * fija con vagas y botón, héroe a dos columnas con la tarjeta de precio,
 * «Tudo o que recebes» con la tarjeta oscura fija al lado, la cinta de sectores
 * a todo el ancho, la línea de tiempo en cinco columnas y las dúvidas en dos.
 */
import { Link } from 'react-router-dom';
import {
  Check, ChevronDown, MapPin, MessageCircle, PenLine, ShieldCheck, Star,
} from 'lucide-react';

import CtaLink from '@/components/CtaLink';

import BotaoComecar from './BotaoComecar';
import SeletorLocais, { usePrecoLocais } from './SeletorLocais';
import {
  COMO_FUNCIONA, FAQ, FINAL, HERO, NOME_PLANO, PRECO, PROVA, QUEM_APARECE, TIMELINE, TUDO,
  WHATSAPP_AFAQ, desglose, formatoCierre, textoVagas,
} from './copy';

const LOGO = '/landing-images/logo.png';
const DISPOSITIVOS = '/landing-images/oferta-pt/tapstar-dispositivos.jpg';

// `w-full`: varias secciones son `flex-col`, y sin él cada hijo encoge a su
// contenido y `mx-auto` lo centra por su cuenta — cada bloque con su propio
// borde izquierdo.
export const COLUNA = 'mx-auto w-full max-w-[640px] px-5 md:max-w-[1160px] md:px-10';
const H2 = 'm-0 text-[28px] font-extrabold leading-tight tracking-[-0.8px] md:text-[40px] md:leading-[1.1] md:tracking-[-1.2px]';

function Kicker({ children }) {
  return (
    <p className="m-0 text-[13px] font-semibold uppercase tracking-[0.6px] text-[#9a5700] md:text-sm md:font-bold">{children}</p>
  );
}

export function Vagas({ restantes, total, cierreAt, enCaja = true }) {
  // La barra BAJA con cada compra: pinta las plazas que quedan, no las ocupadas
  // (feedback de producto, 29/10). Sin el número, llena.
  const quedan = restantes == null ? total : Math.max(0, Math.min(total, restantes));
  const pct = Math.round((quedan / total) * 100);
  return (
    <div className={`flex flex-col gap-3 ${enCaja ? 'rounded-[20px] border border-[#ececf0] bg-[#fafafa] p-[18px]' : 'gap-2'}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[15px] font-bold md:text-sm">{textoVagas(restantes, total)}</span>
        <span className="text-[13px] text-[#5e5e66] md:text-sm">{formatoCierre(cierreAt)}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[#ececf0]" aria-hidden="true">
        <div className="h-2 rounded-full bg-[#feae2c]" style={{ width: `${pct}%` }} />
      </div>
      <p className="m-0 text-[13px] leading-snug text-[#5e5e66]">{HERO.porQue}</p>
    </div>
  );
}

export function Riscado({ valor, className = '' }) {
  return (
    <span
      className={`font-bold text-[#6e6e76] line-through ${className}`}
      style={{ textDecorationColor: '#feae2c', textDecorationThickness: 2 }}
    >
      {valor}
    </span>
  );
}

function Poupas({ valor }) {
  if (!valor) return null;
  return (
    <span className="rounded-full bg-[#e8f6ec] px-3 py-1 text-[13px] font-bold text-[#1d6b34] md:text-sm">
      Poupas {valor}
    </span>
  );
}

// Los ids de compra de la landing. En una constante y no como default de la
// prop: la ventana del plan (`components/ofertaPt/anuncios/VentanaPlano.jsx`)
// reutiliza el héroe y la tarjeta de precio con SUS ids, y el test de CTAs solo
// ve ids escritos como `cta…: 'id'` o `cta="id"`.
export const CTA_LANDING = {
  ctaHero: 'landing.oferta-pt.comprar-hero',
  ctaPreco: 'landing.oferta-pt.comprar-preco',
};

// ── Navegación ──────────────────────────────────────────────────────────────

export function OfertaNav({ restantes, total, comecar, cargando }) {
  return (
    <header className="md:sticky md:top-0 md:z-30 md:border-b md:border-[#f0f0f3] md:bg-white/90 md:backdrop-blur-md">
      <div className={`${COLUNA} flex items-center justify-between gap-3 py-4 md:h-[72px] md:py-0`}>
        <Link to="/" className="flex items-center no-underline" aria-label="MyTapstar">
          <img src={LOGO} alt="MyTapstar" className="h-8 w-auto" />
        </Link>
        <div className="hidden items-center gap-5 md:flex">
          <span className="text-sm font-semibold text-[#9a5700]">{textoVagas(restantes, total)}</span>
          <BotaoComecar
            cta="landing.oferta-pt.comprar-nav"
            comecar={comecar}
            cargando={cargando}
            className="min-h-[44px] rounded-xl px-5 text-[15px] shadow-none"
          />
        </div>
      </div>
    </header>
  );
}

// ── Héroe ───────────────────────────────────────────────────────────────────

/**
 * La tarjeta de precio del héroe de ordenador. La landing personalizada (V6) la
 * monta con su propio `eyebrow` («… · para Café da Praça») y el negocio debajo
 * del botón (`para`).
 */
export function PrecoHero({
  restantes, total, cierreAt, comecar, cargando, ctaComprar, seletor, eyebrow = HERO.eyebrow, para,
}) {
  const preco = usePrecoLocais(seletor?.locais || 1);
  return (
    <div className="flex flex-col gap-4 rounded-[28px] border-2 border-[#feae2c] bg-white p-[30px] shadow-[0_24px_60px_rgba(254,174,44,0.22)]">
      <span className="self-start rounded-full bg-[#fff4dd] px-3 py-1.5 text-[13px] font-bold text-[#6b3d00]">{eyebrow}</span>
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#5e5e66]">{TUDO.valorTotal}</span>
        <Riscado valor={preco.riscado} className="text-[22px]" />
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[56px] font-extrabold leading-[1.05] tracking-[-2px]">{preco.total}</span>
          <Poupas valor={preco.poupas} />
        </div>
        <span className="text-[15px] text-[#5e5e66]">
          {preco.varios ? `por ano · ${seletor.locais} locais · equivale a ${preco.porMes} por mês` : TUDO.porAno}
        </span>
      </div>
      {seletor && <SeletorLocais {...seletor} />}
      <Vagas restantes={restantes} total={total} cierreAt={cierreAt} enCaja={false} />
      <BotaoComecar
        cta={ctaComprar} comecar={comecar} cargando={cargando} para={para}
        className="min-h-[58px] w-full text-lg"
      />
      <span className="text-center text-[13px] text-[#5e5e66]">{TUDO.garantiaTitulo}</span>
    </div>
  );
}

/**
 * `seletor` ({ locais, setLocais }): el mismo selector de locales que la
 * tarjeta de precio y la barra fija, sobre el mismo estado (lo tiene la
 * página), así que los tres se mueven a la vez.
 */
export function HeroOferta({
  restantes, total, cierreAt, comecar, cargando, ctaComprar = CTA_LANDING.ctaHero, seletor,
}) {
  const preco = usePrecoLocais(seletor?.locais || 1);
  return (
    <section className={`${COLUNA} grid gap-8 pb-10 pt-6 md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] md:items-center md:gap-14 md:pb-20 md:pt-[72px]`}>
      <div className="flex flex-col gap-5 md:gap-6">
        <div className="inline-flex items-center gap-2 self-start rounded-full bg-[#fff4dd] px-3 py-1.5 text-[13px] font-semibold text-[#6b3d00] md:hidden">
          <span className="h-2 w-2 shrink-0 rounded-full bg-[#feae2c]" aria-hidden="true" />
          {HERO.eyebrow}
        </div>
        <h1 className="m-0 text-[36px] font-extrabold leading-[1.08] tracking-[-1.2px] md:text-[60px] md:leading-[1.04] md:tracking-[-2.2px]">{HERO.titulo}</h1>
        <p className="m-0 text-[17px] leading-relaxed text-[#4a4a52] md:text-xl">{HERO.texto}</p>
        <div className="flex flex-wrap gap-2 md:gap-2.5">
          {HERO.chips.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-[#f5f5f7] px-2.5 py-[7px] text-[13px] font-medium md:gap-2 md:px-3.5 md:py-[9px] md:text-[15px]">
              <Check className="h-3.5 w-3.5 text-[#9a5700]" aria-hidden="true" />
              {c}
            </span>
          ))}
        </div>
        <p className="m-0 hidden items-center gap-2 text-[15px] font-semibold md:flex">
          <Star className="h-4 w-4 fill-[#feae2c] text-[#feae2c]" aria-hidden="true" />
          {HERO.prova}
        </p>
        {/* En móvil no hay tarjeta al lado: vagas y botón aquí debajo. */}
        <div className="flex flex-col gap-4 md:hidden">
          <Vagas restantes={restantes} total={total} cierreAt={cierreAt} />
          {seletor && (
            <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-2">
              <SeletorLocais {...seletor} />
              <span className="flex flex-col items-end text-right text-[15px] text-[#5e5e66]">
                <Riscado valor={preco.riscado} className="text-sm" />
                <span><span className="text-[22px] font-extrabold text-[#1d1d1f]">{preco.total}</span> /ano</span>
              </span>
            </div>
          )}
          <BotaoComecar cta={ctaComprar} comecar={comecar} cargando={cargando} className="w-full" />
          <p className="m-0 text-center text-[13px] text-[#5e5e66]">{preco.varios ? HERO.letraPequena.replace(PRECO.preco, preco.total) : HERO.letraPequena}</p>
          <p className="m-0 flex items-center justify-center gap-1.5 text-[13px] font-semibold">
            <Star className="h-4 w-4 fill-[#feae2c] text-[#feae2c]" aria-hidden="true" />
            {HERO.prova}
          </p>
        </div>
      </div>
      <div className="hidden md:block">
        <PrecoHero
          restantes={restantes} total={total} cierreAt={cierreAt} comecar={comecar} cargando={cargando}
          ctaComprar={ctaComprar} seletor={seletor}
        />
      </div>
    </section>
  );
}

// ── Quem aparece primeiro ───────────────────────────────────────────────────

export function FaixaQuemAparece() {
  return (
    <section className="bg-[#1d1d1f] py-[52px] md:py-[88px]">
      <div className={`${COLUNA} flex flex-col gap-3.5 md:grid md:grid-cols-[1.3fr_1fr] md:items-end md:gap-12`}>
        <p className="m-0 text-[40px] font-extrabold leading-[1.05] tracking-[-1.4px] text-white md:text-[64px] md:leading-[1.02] md:tracking-[-2.4px]">
          {QUEM_APARECE.titulo} <span className="text-[#feae2c]">{QUEM_APARECE.destaque}</span>
        </p>
        <p className="m-0 text-[17px] leading-relaxed text-[#c7c7cc] md:text-xl">{QUEM_APARECE.texto}</p>
      </div>
    </section>
  );
}

// ── Como funciona ───────────────────────────────────────────────────────────

const ICONES = { ficha: MapPin, respostas: MessageCircle, tapstar: Star, publicacao: PenLine };

export function ComoFunciona() {
  return (
    <section className={`${COLUNA} flex flex-col gap-4 py-12 md:gap-[22px] md:py-20`}>
      <Kicker>{COMO_FUNCIONA.kicker}</Kicker>
      <h2 className={`${H2} md:max-w-[820px]`}>{COMO_FUNCIONA.titulo}</h2>
      <div className="flex flex-col gap-1.5 md:max-w-[720px] md:gap-2">
        <div className="grid h-3 grid-cols-[11fr_1fr] gap-1" aria-hidden="true">
          <span className="rounded-l-full bg-[#feae2c]" />
          <span className="rounded-r-full bg-[#d9d9e0]" />
        </div>
        <div className="flex justify-between gap-2 text-xs font-bold md:text-[13px]">
          <span className="text-[#9a5700]">{COMO_FUNCIONA.nos}</span>
          <span className="text-right text-[#5e5e66]">{COMO_FUNCIONA.tu}</span>
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] md:items-stretch">
        <div className="grid gap-2.5 md:grid-cols-2 md:gap-4">
          {COMO_FUNCIONA.itens.map((it) => {
            const Icone = ICONES[it.icone] || Check;
            return (
              <div
                key={it.titulo}
                className={`flex items-start gap-3 rounded-[18px] border px-4 py-3.5 md:gap-4 md:rounded-[22px] md:p-[22px] ${
                  it.nosso ? 'border-[#ffe2ad] bg-[#fff8ec]' : 'border-[#e5e5ea] bg-white'}`}
              >
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl md:h-12 md:w-12 md:rounded-[14px] ${
                  it.nosso ? 'bg-[#feae2c]' : 'bg-[#f5f5f7]'}`}
                >
                  <Icone className="h-5 w-5 text-[#1d1d1f]" aria-hidden="true" />
                </div>
                <div className="flex min-w-0 flex-col gap-1.5 pt-0.5 md:gap-2.5">
                  <span className="text-base font-bold md:text-[19px] md:leading-tight">{it.titulo}</span>
                  {it.imagem && (
                    <img src={DISPOSITIVOS} alt="Dispositivos Tapstar" loading="lazy"
                      className="my-0.5 block w-[140px] rounded-xl md:hidden" />
                  )}
                  {it.imagem ? (
                    <span className="text-sm font-bold text-[#6b3d00] md:text-[15px]">{it.quem}</span>
                  ) : (
                    <span className={`self-start rounded-full px-2.5 py-1 text-[13px] font-bold md:px-3 md:py-1.5 ${
                      it.nosso ? 'bg-[#fff4dd] text-[#6b3d00]' : 'bg-[#f5f5f7] text-[#4a4a52]'}`}
                    >
                      {it.quem}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {/* Ordenador: los Tapstar en su propio panel, al lado de las tarjetas. */}
        <div className="hidden flex-col overflow-hidden rounded-[22px] border border-[#ffe2ad] bg-[#ffeed3] md:flex">
          <div className="relative min-h-[160px] flex-1">
            <img src={DISPOSITIVOS} alt="Dispositivos Tapstar" loading="lazy"
              className="absolute inset-0 block h-full w-full object-contain" />
          </div>
          <div className="flex items-center gap-2.5 border-t border-[#ffe2ad] bg-white px-[18px] py-3.5">
            <span className="shrink-0 rounded-full bg-[#feae2c] px-2.5 py-1 text-xs font-extrabold">2</span>
            <span className="text-[15px] font-bold text-[#6b3d00]">{COMO_FUNCIONA.escolhe}</span>
          </div>
        </div>
      </div>
      <p className="m-0 text-[15px] font-semibold leading-relaxed md:text-[17px]">{COMO_FUNCIONA.resumo}</p>
    </section>
  );
}

// ── Tudo o que recebes ──────────────────────────────────────────────────────

function Linha({ titulo, texto, valor, imagem = false }) {
  return (
    <div className="flex items-start gap-3 border-b border-[#f0f0f3] py-3.5 last:border-b-0 md:gap-3.5 md:py-4">
      <span className="mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#fff4dd] md:h-[26px] md:w-[26px]">
        <Check className="h-3.5 w-3.5 text-[#9a5700]" aria-hidden="true" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 md:gap-1">
        <span className="text-[15px] font-bold md:text-[17px]">{titulo}</span>
        {texto && <span className="text-[13px] leading-snug text-[#5e5e66] md:text-[15px]">{texto}</span>}
        {/* Solo en móvil: en ordenador los Tapstar ya salen en su panel de
            «Como funciona». */}
        {imagem && (
          <img src={DISPOSITIVOS} alt="Dispositivos Tapstar" loading="lazy"
            className="mt-1.5 block h-24 w-24 rounded-xl object-cover md:hidden" />
        )}
      </div>
      <span className="whitespace-nowrap text-[15px] font-semibold text-[#4a4a52] md:text-[17px]">{valor}</span>
    </div>
  );
}

function Grupo({ titulo, nota = null, primeiro = false, children }) {
  return (
    <div className={primeiro ? 'pt-1.5' : 'mt-1 border-t-2 border-[#f0f0f3] pt-5'}>
      <span className="text-xs font-extrabold uppercase tracking-[0.6px] text-[#9a5700] md:text-[13px]">
        {titulo}
        {nota && <span className="font-semibold normal-case tracking-normal text-[#5e5e66]"> {nota}</span>}
      </span>
      {children}
    </div>
  );
}

function OQueInclui() {
  return (
    <details className="group mb-3.5 md:ml-10">
      <summary className="flex min-h-[50px] cursor-pointer list-none items-center justify-between gap-2.5 rounded-[14px] border-[1.5px] border-[#ffe2ad] bg-[#fff8ec] pl-4 pr-2 text-[15px] font-bold text-[#6b3d00] [&::-webkit-details-marker]:hidden">
        {TUDO.incluiTitulo}
        <ChevronDown className="h-5 w-5 transition-transform group-open:rotate-180" aria-hidden="true" />
      </summary>
      <div className="mt-2.5 grid gap-3 text-[13px] md:grid-cols-[1fr_1.4fr] md:text-sm">
        <div className="flex flex-col gap-2.5 rounded-2xl border border-[#ececf0] bg-[#f5f5f7] p-4">
          <span className="text-xs font-bold uppercase tracking-[0.5px] text-[#5e5e66]">{TUDO.jaTens}</span>
          {TUDO.jaTensItens.map((i) => <span key={i}>{i}</span>)}
        </div>
        <div className="flex flex-col gap-2.5 rounded-2xl border border-[#ffe2ad] bg-[#fff8ec] p-4">
          <span className="text-xs font-bold uppercase tracking-[0.5px] text-[#9a5700]">{TUDO.comBusiness}</span>
          {TUDO.comBusinessItens.map((i) => (
            <span key={i} className="flex items-start gap-1.5">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#9a5700]" aria-hidden="true" />
              {i}
            </span>
          ))}
        </div>
      </div>
    </details>
  );
}

function Garantia({ className = '' }) {
  return (
    <div className={`flex items-start gap-3.5 rounded-[22px] bg-[#fff4dd] p-[22px] ${className}`}>
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white">
        <ShieldCheck className="h-6 w-6 text-[#1d6b34]" aria-hidden="true" />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-lg font-extrabold">{TUDO.garantiaTitulo}</span>
        <span className="text-[15px] leading-relaxed text-[#4a3200]">{TUDO.garantiaTexto}</span>
      </div>
    </div>
  );
}

/**
 * `seletor` ({ locais, setLocais }): el selector de locales va en las dos
 * tarjetas de precio y todo pasa a ser de N locales: el precio con los tramos
 * de la tarifa, cada partida del desglose y el «valor total» tachado
 * (`desglose`, `VALOR_POR_LOCAL` en `copy.js`), con su «Poupas».
 */
export function TudoOQueRecebes({ comecar, cargando, ctaComprar = CTA_LANDING.ctaPreco, seletor }) {
  const locais = seletor?.locais || 1;
  const preco = usePrecoLocais(locais);
  const partidas = desglose(locais);
  const porAno = preco.varios
    ? `por ano · ${seletor.locais} locais · equivale a ${preco.porMes} por mês`
    : TUDO.porAno;
  const ivaComVies = preco.varios
    ? `Empresa com NIF português registado no VIES: ${preco.total}, sem IVA.`
    : TUDO.ivaComVies;
  const ivaSemVies = preco.varios ? `Sem registo no VIES: ${preco.comIva} com IVA (23 %).` : TUDO.ivaSemVies;
  return (
    <section id="pagar" className="bg-[#f5f5f7] pb-12 pt-12 md:py-20">
      <div className={`${COLUNA} grid gap-4 md:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)] md:items-start md:gap-7`}>
        <div className="overflow-hidden rounded-[26px] border-2 border-[#feae2c] bg-white shadow-[0_18px_48px_rgba(254,174,44,0.18)] md:rounded-[28px] md:border-0 md:shadow-none">
          <div className="px-5 pb-2 pt-[22px] md:px-8 md:pb-4 md:pt-[30px]">
            <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#9a5700] md:text-[13px]">{NOME_PLANO}</span>
            <h2 className={`${H2} mt-1.5 ${locais > 1 ? 'mb-1' : 'mb-4'}`}>{TUDO.titulo}</h2>
            {locais > 1 && (
              <p className="mb-4 mt-0 text-[15px] font-semibold text-[#9a5700]" data-testid="desglose-locais">
                Para {locais} locais
              </p>
            )}
            <Grupo titulo={TUDO.nosTitulo} nota={TUDO.nosNota} primeiro>
              {partidas.nos.map((l) => <Linha key={l.titulo} {...l} />)}
            </Grupo>
            <Grupo titulo={TUDO.tuTitulo} nota={TUDO.tuNota}>
              <Linha {...partidas.plataforma} />
              <OQueInclui />
            </Grupo>
            <Grupo titulo={TUDO.ofertaTitulo}>
              {partidas.oferta.map((l, i) => <Linha key={l.titulo} {...l} imagem={i === 0} />)}
            </Grupo>
          </div>
          {/* Móvil: el precio cierra la misma tarjeta, en el bloque oscuro del
              diseño (`V4_Main`). La garantía va en su propia tarjeta, debajo. */}
          <div className="flex flex-col gap-1.5 bg-[#1d1d1f] px-5 py-[22px] text-white md:hidden">
            {seletor && <SeletorLocais {...seletor} escuro className="mb-2" />}
            <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#c7c7cc]">{TUDO.valorTotal}</span>
            <span
              className="text-[22px] font-bold text-[#f5f5f7] line-through"
              style={{ textDecorationColor: '#feae2c', textDecorationThickness: 2 }}
            >
              {preco.riscado}
            </span>
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-[44px] font-extrabold tracking-[-1.4px]">{preco.total}</span>
              <Poupas valor={preco.poupas} />
            </div>
            <span className="text-[15px] text-[#c7c7cc]">{porAno}</span>
            <span className="text-sm font-semibold leading-snug">{ivaComVies}</span>
            <span className="text-sm leading-snug text-[#c7c7cc]">{ivaSemVies}</span>
            <BotaoComecar
              cta={ctaComprar} comecar={comecar} cargando={cargando}
              className="mt-3 min-h-[54px] w-full rounded-[14px] shadow-none"
            />
          </div>
        </div>
        <Garantia className="px-5 py-6 md:hidden" />

        {/* Ordenador: la tarjeta oscura del precio, fija al bajar. */}
        <div className="sticky top-24 hidden flex-col gap-4 md:flex">
          <div className="flex flex-col gap-2 rounded-[28px] border-2 border-[#feae2c] bg-[#1d1d1f] p-[30px] text-white shadow-[0_24px_60px_rgba(254,174,44,0.2)]">
            {seletor && <SeletorLocais {...seletor} escuro className="mb-2" />}
            <span className="text-xs font-bold uppercase tracking-[0.6px] text-[#c7c7cc]">{TUDO.valorTotal}</span>
            <span
              className="text-2xl font-bold text-[#f5f5f7] line-through"
              style={{ textDecorationColor: '#feae2c', textDecorationThickness: 2 }}
            >
              {preco.riscado}
            </span>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[60px] font-extrabold leading-[1.05] tracking-[-2px]">{preco.total}</span>
              <Poupas valor={preco.poupas} />
            </div>
            <span className="text-base text-[#c7c7cc]">{porAno}</span>
            <span className="mt-1.5 text-[15px] font-semibold leading-snug">{ivaComVies}</span>
            <span className="text-[15px] leading-snug text-[#c7c7cc]">{ivaSemVies}</span>
            <BotaoComecar
              cta={ctaComprar} comecar={comecar} cargando={cargando}
              className="mt-3.5 min-h-[58px] w-full text-lg shadow-none"
            />
          </div>
          <Garantia />
        </div>
      </div>
    </section>
  );
}

// ── Prova social ────────────────────────────────────────────────────────────

export function ProvaSocial() {
  const faixa = [...PROVA.setores, ...PROVA.setores];
  return (
    <section className="bg-[#f5f5f7] pb-12 pt-8 md:pb-20 md:pt-2">
      <div className={`${COLUNA} flex flex-col items-center gap-1.5 text-center`}>
        <span className="text-[44px] font-extrabold leading-none tracking-[-1.4px] md:text-[72px] md:tracking-[-2.5px]">{PROVA.numero}</span>
        <span className="text-[17px] font-semibold text-[#4a4a52] md:text-xl">{PROVA.texto}</span>
        <span className="text-[15px] text-[#5e5e66] md:text-base">{PROVA.sub}</span>
      </div>
      {/* A todo el ancho y difuminada en los dos bordes, como el diseño: así
          la cinta queda simétrica bajo el bloque centrado de arriba. */}
      <div
        className="my-7 overflow-hidden"
        aria-hidden="true"
        style={{
          maskImage: 'linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)',
          WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)',
        }}
      >
        <div className="flex w-max animate-[oferta-pt-faixa_50s_linear_infinite] gap-3.5 motion-reduce:animate-none">
          {faixa.map((s, i) => (
            <span key={`${s}-${i}`} className="whitespace-nowrap rounded-full border border-[#ececf0] bg-white px-5 py-3 text-[15px] font-semibold text-[#4a4a52] md:py-3.5 md:text-base">
              {s}
            </span>
          ))}
        </div>
      </div>
      <div className={`${COLUNA} flex flex-col gap-3`}>
        {/* Las tres en una fila también en móvil, como el diseño. `min-w-0` y
            `break-words` dejan que el texto parta dentro de su columna en vez
            de empujarla, y la cifra escala con el ancho (clamp) para que
            «+40%» quepa aunque la pantalla sea de 320 px. */}
        <div className="grid grid-cols-3 gap-2 md:gap-4">
          {PROVA.metricas.map((m) => (
            <div key={m.texto} className="flex min-w-0 flex-col gap-1 rounded-2xl bg-white px-3 py-3.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] md:gap-1.5 md:rounded-[22px] md:p-[26px] md:shadow-none">
              <span className="whitespace-nowrap text-[clamp(20px,7vw,26px)] font-extrabold tracking-[-0.8px] text-[#1d6b34] md:text-5xl md:tracking-[-1.5px]">{m.valor}</span>
              <span className="break-words text-[13px] leading-[1.35] text-[#4a4a52] md:text-base md:leading-snug">{m.texto}</span>
            </div>
          ))}
        </div>
        <p className="m-0 text-center text-xs text-[#5e5e66] md:text-sm">{PROVA.nota}</p>
      </div>
      <style>{'@keyframes oferta-pt-faixa{from{transform:translateX(0)}to{transform:translateX(-50%)}}'}</style>
    </section>
  );
}

// ── Depois de pagares ───────────────────────────────────────────────────────

export function Timeline({ semGmb }) {
  const passos = TIMELINE.passos(semGmb);
  return (
    <section className={`${COLUNA} flex flex-col gap-5 py-12 md:gap-7 md:pb-16 md:pt-20`}>
      <h2 className={H2}>{TIMELINE.titulo}</h2>
      <ol className="m-0 grid list-none gap-0 p-0 md:grid-cols-5">
        {passos.map((p, i) => (
          <li key={p.quando} className="relative flex gap-3.5 pb-5 md:flex-col md:gap-2.5 md:pb-0 md:pr-5">
            <div className="flex flex-col items-center md:flex-row">
              <span className="h-4 w-4 shrink-0 rounded-full bg-[#feae2c]" aria-hidden="true" />
              {i < passos.length - 1 && (
                <span className="mt-1 w-0.5 flex-1 bg-[#ffe2ad] md:mt-0 md:h-0.5 md:w-auto" aria-hidden="true" />
              )}
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[15px] font-bold md:text-base">{p.quando}</span>
              <span className="text-sm leading-relaxed text-[#4a4a52] md:text-[15px]">{p.texto}</span>
            </div>
          </li>
        ))}
      </ol>
      <p className="m-0 text-[15px] font-semibold md:font-normal md:text-[#5e5e66]">{TIMELINE.nota}</p>
    </section>
  );
}

// ── Dúvidas ─────────────────────────────────────────────────────────────────

/**
 * Acordeón propio con `<details>`, como el diseño V4, y no `FaqAcordeon` de la
 * landing V3: aquel solo esconde las respuestas con una regla de
 * `landingV3.css` (`.landing-v3 .v3-faq-body`) que esta página no carga, y sin
 * ella las respuestas salían siempre abiertas. `<details>` abre y cierra solo,
 * sin JavaScript, y un lector de pantalla ya lo anuncia como desplegable.
 *
 * En ordenador van en dos columnas, repartidas en orden (la primera mitad a la
 * izquierda) para que se lean de arriba abajo.
 */
function Pergunta({ q, a }) {
  return (
    <details className="group border-b border-[#ececf0]">
      <summary className="flex min-h-[56px] cursor-pointer list-none items-center gap-4 py-4 [&::-webkit-details-marker]:hidden">
        <span className="flex-1 text-base font-bold md:text-[17px] md:font-semibold">{q}</span>
        <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#f5f5f7] text-[#4a4a52]">
          <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
        </span>
      </summary>
      <p className="m-0 pb-5 pr-11 text-[15px] leading-relaxed text-[#4a4a52]">{a}</p>
    </details>
  );
}

/**
 * `soloEstas` (opcional): las preguntas, por su texto, que se enseñan y en ese
 * orden. La ventana del plan dentro de la app lleva 5 (6 sin GMB) y no las 9.
 */
export function Duvidas({ semGmb, soloEstas }) {
  const todas = FAQ.preguntas(semGmb);
  const preguntas = soloEstas
    ? soloEstas.map((q) => todas.find((f) => f.q === q)).filter(Boolean)
    : todas;
  const metade = Math.ceil(preguntas.length / 2);
  const colunas = [preguntas.slice(0, metade), preguntas.slice(metade)];
  return (
    <section id="duvidas" className={`${COLUNA} flex flex-col gap-4 py-12 md:pb-[72px] md:pt-6`}>
      <h2 className={H2}>{FAQ.titulo}</h2>
      <div className="grid md:grid-cols-2 md:items-start md:gap-x-12">
        {colunas.map((col, i) => (
          <div key={i} className={i === 0 ? 'border-t border-[#ececf0] md:border-t-0' : 'md:border-t-0'}>
            {col.map((f) => <Pergunta key={f.q} {...f} />)}
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Cierre ──────────────────────────────────────────────────────────────────

export function CtaFinal({ restantes, cierreAt, comecar, cargando }) {
  return (
    <section className={`${COLUNA} pb-12 md:pb-16`}>
      <div className="flex flex-col items-center gap-4 rounded-[28px] border border-[#ececf0] bg-[#fafafa] px-6 py-10 text-center md:rounded-[32px] md:px-10 md:py-14">
        <h2 className={H2}>{FINAL.restam(restantes)}</h2>
        <p className="m-0 text-[17px] text-[#5e5e66] md:text-lg">{FINAL.termina(cierreAt)}</p>
        <BotaoComecar
          cta="landing.oferta-pt.comprar-final" comecar={comecar} cargando={cargando}
          className="mt-2 min-h-[58px] w-full text-lg md:w-auto md:px-8"
        />
        <p className="m-0 flex min-h-[44px] flex-wrap items-center justify-center gap-1.5 text-base font-semibold">
          {FINAL.duvidas}
          <CtaLink
            as="a"
            cta="landing.oferta-pt.whatsapp"
            href={WHATSAPP_AFAQ}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-bold text-[#9a5700] underline-offset-2 hover:underline"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            {FINAL.falaCom}
          </CtaLink>
        </p>
      </div>
    </section>
  );
}

// ── Pie ─────────────────────────────────────────────────────────────────────

export function OfertaFooter() {
  return (
    <footer className="border-t border-[#f0f0f3] bg-white">
      <div className={`${COLUNA} flex flex-col gap-4 py-8 md:flex-row md:items-center md:justify-between md:pb-12 md:pt-6`}>
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
          <img src={LOGO} alt="MyTapstar" className="h-7 w-auto self-start" />
          <span className="text-sm text-[#5e5e66]">{FINAL.pagamento}</span>
        </div>
        <div className="flex flex-col gap-2 md:items-end">
          <nav className="flex flex-wrap gap-x-5 gap-y-2 text-[13px]" aria-label="Legal">
            <Link to="/privacy" className="text-[#5e5e66] hover:text-[#1d1d1f]">Privacidade</Link>
            <Link to="/terms" className="text-[#5e5e66] hover:text-[#1d1d1f]">Termos</Link>
            <Link to="/cookies" className="text-[#5e5e66] hover:text-[#1d1d1f]">Cookies</Link>
            <Link to="/legal-notice" className="text-[#5e5e66] hover:text-[#1d1d1f]">Aviso legal</Link>
          </nav>
          <span className="text-[13px] text-[#6e6e76]">Tapstar · Speed Data Technologies, S.L.</span>
        </div>
      </div>
    </footer>
  );
}
